// @file Post sorting related functions for all formats
import { ww } from 'bits/global'
import { $ } from 'bits/jquery'
import sortBy from 'lodash.sortby'
import type { Cid, Post } from 'types'
import { Store } from 'vuex'

// Assumption: by the time surface/sorter (i.e. this file) is used,
// the store would have been initialized.
interface SurfaceXstore {
  store: Store<any>
}
const store = (): Store<any> => (ww.xstore as unknown as SurfaceXstore)?.store

function getAbsoluteSortIndex(ts: Date | number = new Date(), wallCreatedAt = store().getters.wallCreatedAt): number {
  const timestamp = ts instanceof Date ? ts.getTime() : ts
  const absSortIndex = 1024 * (timestamp - new Date(wallCreatedAt).getTime())
  const postMaxAbsSortIndexWithBuffer = store().getters['post/postMaxAbsSortIndex'] + 1024
  return Math.max(absSortIndex, postMaxAbsSortIndexWithBuffer)
}

function getSortIndex(): number {
  const postAtBottom = store().getters.newPostLocation === 'bottom' && store().getters.canConfigNewPostLocation
  const absSortIndex = getAbsoluteSortIndex()
  return postAtBottom ? -absSortIndex : absSortIndex
}

function getTopSortIndex(): number {
  return getAbsoluteSortIndex()
}

function getBottomSortIndex(): number {
  return -getAbsoluteSortIndex()
}

interface ComputeNewPostIndexOptions {
  sectionId?: number
  prevId?: string | null
  nextId?: string | null
}

function computePostIndexFromPost(
  post,
  options: ComputeNewPostIndexOptions = {},
): { sortIndex: number | null; sectionId?: number } {
  const $post = $(post)
  const sectionId = options.sectionId
  let $prev
  if (options.prevId === undefined) {
    $prev = $post.prev('.wish')
  } else if (options.prevId != null) {
    $prev = $(`#${options.prevId}`)
  } else {
    $prev = $([])
  }

  let $next
  if (options.nextId === undefined) {
    $next = $post.next('.wish')
  } else if (options.nextId != null) {
    $next = $(`#${options.nextId}`)
  } else {
    $next = $([])
  }

  let nextIndex: number | null = null
  let prevIndex: number | null = null
  let sortIndex: number | null = null
  if ($next.length === 0 && $prev.length === 0) {
    sortIndex = sectionId === null ? null : getSortIndex()
  } else {
    if ($next.length === 0) {
      nextIndex = getBottomSortIndex()
    } else if ($prev.length === 0) {
      prevIndex = getTopSortIndex()
    }
    if (nextIndex == null) {
      nextIndex = parseInt($next.attr('data-rank'), 10)
    }
    if (prevIndex == null) {
      prevIndex = parseInt($prev.attr('data-rank'), 10)
    }
    if (nextIndex != null && prevIndex != null) {
      sortIndex = Math.round((prevIndex + nextIndex) / 2)
    } else {
      sortIndex = null
    }
  }

  return {
    sortIndex,
    sectionId,
  }
}

function computeAndSaveNewPostIndex(post, options: ComputeNewPostIndexOptions = {}): void {
  const { sortIndex, sectionId } = computePostIndexFromPost(post, options)
  const $post = $(post)

  if (!sortIndex) return
  store().dispatch('post/sortedPost', {
    postCid: $post.data('post-cid'),
    sortIndex: sortIndex,
    sectionId: sectionId,
  })
}

//TODO: Remove and switch to wish order array
function sortPostsBySortIndex(posts: Post[]): Post[] {
  return sortBy(posts, (post: Post): number | undefined => {
    if (post.sort_index) {
      return post.sort_index
    }
    const oldRank = parseInt((post.rank || '') + new Date(post.created_at as string).getTime(), 10)
    return Math.abs(getAbsoluteSortIndex(oldRank))
  }).reverse()
}

function sortPostsByPostOrderArray(posts: Post[], postOrder: Cid[]): Post[] {
  return sortBy(posts, (post: Post): number | undefined => {
    let index = postOrder.indexOf(post.cid)
    if (index === -1) {
      index = posts.length // Put post at the top
    }
    return index
  }).reverse()
}

function recomputeTimelineSortIndex(curSortIndex: number | undefined, sortedPosts: Post[]): number | undefined {
  if (!curSortIndex || sortedPosts.length < 1) {
    return curSortIndex
  }

  // check if a post with sort_index === curSortIndex already exists. If so, compute new sort_index that goes after it
  const beforePostIndex = sortedPosts.findIndex((p) => p.sort_index === curSortIndex)

  // no collision
  if (beforePostIndex < 0) {
    return curSortIndex
  }

  const beforePostId = sortedPosts[beforePostIndex].id
  const afterPostId =
    beforePostIndex === sortedPosts.length - 1 && sortedPosts.length === 1 ? null : sortedPosts[beforePostIndex + 1].id

  // Timeline sort index is from smallest on the left to largest to the right
  // while it's from largest to smallest on other formats
  const { sortIndex } = computePostIndexFromPost(null, {
    prevId: `wish-${afterPostId}`,
    nextId: `wish-${beforePostId}`,
  })

  return sortIndex || 0
}

export {
  getTopSortIndex,
  getBottomSortIndex,
  getSortIndex,
  computePostIndexFromPost,
  computeAndSaveNewPostIndex,
  //TODO: Remove and switch to wish order array
  sortPostsBySortIndex,
  sortPostsByPostOrderArray,
  recomputeTimelineSortIndex,
}
